<template>
	<div class="carbon-lab-page">
		<q-header elevated>
			<Toolbar />
		</q-header>

		<section class="hero-section">
			<div class="hero-content">
				<div class="eyebrow">OPEN TEACHING TOOL · APPAREL LCA</div>
				<h1>Dynimos Apparel Carbon Lab</h1>
				<p class="hero-copy">
					Build one garment, follow its life cycle, and see every carbon calculation change in real time.
				</p>
			</div>
		</section>

		<main class="lab-shell">
			<q-banner rounded class="formula-banner">
				<template #avatar>
					<q-icon name="functions" color="primary" size="32px" />
				</template>
				<div class="text-weight-bold">The idea behind every result</div>
				<div>
					Carbon emissions = activity data × emission factor. This lab keeps both numbers visible so learners can ask
					“what changed?” and “why?”
				</div>
			</q-banner>

			<q-tabs
				v-model="activeTab"
				dense
				no-caps
				align="justify"
				active-color="primary"
				indicator-color="accent"
				class="lab-tabs bg-white"
			>
				<q-tab name="garment" icon="checkroom" label="1. Build a garment" />
				<q-tab name="lifecycle" icon="route" label="2. Life-cycle choices" />
				<q-tab name="results" icon="insights" label="3. Read the result" />
				<q-tab name="challenge" icon="emoji_events" label="4. Carbon challenge" />
			</q-tabs>

			<q-tab-panels v-model="activeTab" animated class="panel-wrap">
				<q-tab-panel name="garment">
					<div class="section-heading">
						<div>
							<div class="step-kicker">QUICK BRIEF</div>
							<h2>What are we making?</h2>
							<p>Choose a starting garment, then create a fiber blend that adds to 100%.</p>
						</div>
						<q-chip outline color="primary">Functional unit: one garment over its lifetime</q-chip>
					</div>

					<div class="two-column-grid">
						<q-card flat bordered class="input-card">
							<q-card-section>
								<div class="card-title"><q-icon name="checkroom" /> Garment</div>
								<q-select
									v-model="scenario.garmentType"
									:options="GARMENT_PRESETS"
									emit-value
									map-options
									outlined
									label="Garment type"
									@update:model-value="applyGarmentPreset"
								/>
								<q-input
									v-model.number="scenario.weightGrams"
									type="number"
									min="20"
									max="5000"
									outlined
									label="Finished garment weight"
									suffix="g"
									class="q-mt-md"
								/>
								<div class="micro-explanation">
									A heavier garment needs more fiber. Cutting loss is added in the next step, so finished weight and input
									weight remain different.
								</div>
							</q-card-section>
						</q-card>

						<q-card flat bordered class="input-card">
							<q-card-section>
								<div class="row items-center justify-between q-mb-sm">
									<div class="card-title"><q-icon name="texture" /> Material mix</div>
									<q-btn flat dense no-caps color="primary" icon="add" label="Add fiber" @click="addMaterial" />
								</div>

								<div v-for="(row, index) in scenario.materials" :key="index" class="material-row">
									<q-select
										v-model="row.material"
										:options="materialOptions"
										emit-value
										map-options
										outlined
										dense
										label="Fiber"
										class="material-select"
									/>
									<q-input
										v-model.number="row.percentage"
										type="number"
										min="0"
										max="100"
										outlined
										dense
										suffix="%"
										aria-label="Material percentage"
										class="material-percent"
									/>
									<q-btn
										flat
										round
										dense
										icon="close"
										aria-label="Remove material"
										:disable="scenario.materials.length === 1"
										@click="removeMaterial(index)"
									/>
								</div>

								<div class="mix-total" :class="{ 'mix-total--error': !result.isMaterialMixValid }">
									<span>Material total</span>
									<strong>{{ formatNumber(result.materialTotal, 0) }}%</strong>
								</div>
								<q-linear-progress
									rounded
									size="10px"
									:model-value="Math.min(result.materialTotal / 100, 1)"
									:color="result.isMaterialMixValid ? 'positive' : 'warning'"
								/>
								<div v-if="!result.isMaterialMixValid" class="validation-copy">
									Adjust the percentages to exactly 100%. Preview calculations are normalized until then.
								</div>
							</q-card-section>
						</q-card>
					</div>

					<div class="panel-actions">
						<q-btn unelevated no-caps color="primary" icon-right="arrow_forward" label="Choose life-cycle data" @click="goTo('lifecycle')" />
					</div>
				</q-tab-panel>

				<q-tab-panel name="lifecycle">
					<div class="section-heading">
						<div>
							<div class="step-kicker">EXPLORE THE SYSTEM</div>
							<h2>How does the garment move through its life cycle?</h2>
							<p>Change one assumption at a time and watch the live footprint at the bottom of each card.</p>
						</div>
					</div>

					<div class="lifecycle-grid">
						<q-card flat bordered class="input-card stage-card">
							<q-card-section>
								<div class="stage-number">01</div>
								<div class="card-title">Materials & making</div>
								<q-select
									v-model="scenario.manufacturingRegion"
									:options="MANUFACTURING_REGIONS"
									emit-value
									map-options
									outlined
									label="Manufacturing electricity"
									class="q-mt-md"
								/>
								<label class="slider-label">
									<span>Process energy</span><strong>{{ scenario.manufacturingEnergyKwhPerKg }} kWh/kg</strong>
								</label>
								<q-slider v-model="scenario.manufacturingEnergyKwhPerKg" :min="2" :max="20" :step="0.5" label color="primary" />
								<label class="slider-label">
									<span>Cutting loss</span><strong>{{ scenario.cuttingWastePercent }}%</strong>
								</label>
								<q-slider v-model="scenario.cuttingWastePercent" :min="0" :max="35" :step="1" label color="primary" />
								<div class="stage-live">{{ stageValue('materials', 'manufacturing') }} kg CO2e</div>
							</q-card-section>
						</q-card>

						<q-card flat bordered class="input-card stage-card">
							<q-card-section>
								<div class="stage-number">02</div>
								<div class="card-title">Transport</div>
								<q-select
									v-model="scenario.transportMode"
									:options="TRANSPORT_MODES"
									emit-value
									map-options
									outlined
									label="Main freight mode"
									class="q-mt-md"
								/>
								<q-input
									v-model.number="scenario.transportDistanceKm"
									type="number"
									min="0"
									max="30000"
									outlined
									label="Distance"
									suffix="km"
									class="q-mt-md"
								/>
								<div class="micro-explanation">The model uses tonne-kilometres, so freight mode and garment mass both matter.</div>
								<div class="stage-live">{{ stageValue('transport') }} kg CO2e</div>
							</q-card-section>
						</q-card>

						<q-card flat bordered class="input-card stage-card">
							<q-card-section>
								<div class="stage-number">03</div>
								<div class="card-title">Use phase</div>
								<div class="compact-inputs q-mt-md">
									<q-input v-model.number="scenario.lifetimeWears" type="number" min="1" outlined label="Lifetime wears" />
									<q-input v-model.number="scenario.wearsBetweenWashes" type="number" min="1" outlined label="Wears between washes" />
								</div>
								<q-select
									v-model="scenario.washTemperature"
									:options="WASH_TEMPERATURES"
									emit-value
									map-options
									outlined
									label="Wash temperature"
									class="q-mt-md"
								/>
								<label class="slider-label">
									<span>Loads tumble dried</span><strong>{{ scenario.dryerSharePercent }}%</strong>
								</label>
								<q-slider v-model="scenario.dryerSharePercent" :min="0" :max="100" :step="5" label color="primary" />
								<div class="stage-live">{{ stageValue('use') }} kg CO2e · {{ result.washCycles }} washes</div>
							</q-card-section>
						</q-card>

						<q-card flat bordered class="input-card stage-card">
							<q-card-section>
								<div class="stage-number">04</div>
								<div class="card-title">End of life</div>
								<q-select
									v-model="scenario.endOfLife"
									:options="END_OF_LIFE_OPTIONS"
									emit-value
									map-options
									outlined
									label="Final pathway"
									class="q-mt-md"
								/>
								<div class="micro-explanation">
									To keep comparisons transparent, reuse and recycling receive no avoided-production credit in this model.
								</div>
								<div class="stage-live">{{ stageValue('endOfLife') }} kg CO2e</div>
							</q-card-section>
						</q-card>
					</div>

					<div class="panel-actions panel-actions--split">
						<q-btn flat no-caps color="primary" icon="arrow_back" label="Back to garment" @click="goTo('garment')" />
						<q-btn unelevated no-caps color="primary" icon-right="insights" label="Generate results" @click="goTo('results')" />
					</div>
				</q-tab-panel>

				<q-tab-panel name="results">
					<div class="section-heading">
						<div>
							<div class="step-kicker">LEARNER PREVIEW</div>
							<h2>Where does the carbon come from?</h2>
							<p>These results update immediately. The default functional unit is one garment over its modeled lifetime.</p>
						</div>
						<q-btn outline no-caps color="primary" icon="restart_alt" label="Reset example" @click="resetScenario" />
					</div>

					<q-banner v-if="!result.isMaterialMixValid" rounded class="warning-banner q-mb-lg">
						Material percentages total {{ formatNumber(result.materialTotal, 0) }}%. Results are temporarily normalized;
						return to step 1 for a valid 100% blend.
					</q-banner>

					<div class="result-summary-grid">
						<q-card flat class="metric-card metric-card--primary">
							<div class="metric-label">Life-cycle total</div>
							<div class="metric-value">{{ formatNumber(result.total) }}</div>
							<div class="metric-unit">kg CO2e / garment</div>
						</q-card>
						<q-card flat class="metric-card">
							<div class="metric-label">Per wear</div>
							<div class="metric-value">{{ formatNumber(result.perWear, 3) }}</div>
							<div class="metric-unit">kg CO2e / wear</div>
						</q-card>
						<q-card flat class="metric-card">
							<div class="metric-label">Carbon hotspot</div>
							<div class="metric-value metric-value--text">{{ result.biggestStage.label }}</div>
							<div class="metric-unit">{{ formatNumber(result.biggestStage.emissions) }} kg CO2e</div>
						</q-card>
						<q-card flat class="metric-card">
							<div class="metric-label">Fiber input</div>
							<div class="metric-value">{{ formatNumber(result.inputWeightKg, 3) }}</div>
							<div class="metric-unit">kg before cutting loss</div>
						</q-card>
					</div>

					<div class="chart-grid">
						<q-card flat bordered class="chart-card">
							<q-card-section>
								<div class="card-title">Life-cycle share</div>
								<apexchart type="donut" height="330" :options="stageChartOptions" :series="stageChartSeries" />
							</q-card-section>
						</q-card>
						<q-card flat bordered class="chart-card">
							<q-card-section>
								<div class="card-title">Material contribution</div>
								<apexchart type="bar" height="330" :options="materialChartOptions" :series="materialChartSeries" />
							</q-card-section>
						</q-card>
					</div>

					<q-card flat bordered class="formula-card">
						<q-card-section>
							<div class="row items-center justify-between q-mb-md">
								<div>
									<div class="card-title">Show the work</div>
									<div class="text-grey-7">Each row is activity data × emission factor.</div>
								</div>
								<q-chip color="blue-1" text-color="primary" icon="visibility">Transparent model</q-chip>
							</div>
							<div class="formula-table-wrap">
								<table class="formula-table">
									<thead>
										<tr>
											<th>Stage</th>
											<th>Activity data</th>
											<th>Emission factor</th>
											<th>Result</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="row in result.calculationRows" :key="row.stage">
											<td><strong>{{ row.stage }}</strong><small>{{ row.explanation }}</small></td>
											<td>{{ row.activity }}</td>
											<td>{{ row.factor }}</td>
											<td><strong>{{ formatNumber(row.result, 3) }} kg CO2e</strong></td>
										</tr>
									</tbody>
								</table>
							</div>
						</q-card-section>
					</q-card>

					<q-banner rounded class="hotspot-banner q-mt-lg">
						<template #avatar><q-icon name="tips_and_updates" color="amber-9" size="32px" /></template>
						<strong>Hotspot question:</strong> {{ hotspotHint }}
					</q-banner>

					<div class="panel-actions panel-actions--split">
						<q-btn flat no-caps color="primary" icon="tune" label="Change assumptions" @click="goTo('lifecycle')" />
						<q-btn unelevated no-caps color="primary" icon-right="emoji_events" label="Try the 30% challenge" @click="goTo('challenge')" />
					</div>
				</q-tab-panel>

				<q-tab-panel name="challenge">
					<div class="challenge-hero">
						<div>
							<div class="step-kicker">CARBON DESIGN CHALLENGE</div>
							<h2>Can you cut the footprint by 30%?</h2>
							<p>Use the design moves below or return to the model and invent your own strategy.</p>
						</div>
						<div class="challenge-score" :class="{ 'challenge-score--complete': comparison.challengeComplete }">
							<div>{{ comparison.reductionPercent > 0 ? comparison.reductionPercent : 0 }}%</div>
							<span>reduction</span>
						</div>
					</div>

					<q-linear-progress
						rounded
						size="18px"
						:model-value="challengeProgress"
						:color="comparison.challengeComplete ? 'positive' : 'primary'"
						class="q-mb-lg"
					/>

					<q-banner v-if="comparison.challengeComplete" rounded class="success-banner q-mb-lg">
						<template #avatar><q-icon name="workspace_premium" size="36px" /></template>
						<strong>Challenge complete!</strong> Your scenario saves {{ formatNumber(comparison.savings) }} kg CO2e per garment
						compared with the baseline.
					</q-banner>

					<div class="comparison-grid">
						<q-card flat bordered class="comparison-card">
							<div class="comparison-label">Baseline</div>
							<div class="comparison-value">{{ formatNumber(baselineResult.total) }}</div>
							<div>kg CO2e / garment</div>
						</q-card>
						<q-icon name="arrow_forward" size="30px" color="grey-6" />
						<q-card flat bordered class="comparison-card comparison-card--current">
							<div class="comparison-label">Your scenario</div>
							<div class="comparison-value">{{ formatNumber(result.total) }}</div>
							<div>kg CO2e / garment</div>
						</q-card>
					</div>

					<div class="action-grid">
						<button type="button" class="action-card" @click="applyAction('materials')">
							<q-icon name="recycling" size="32px" color="positive" />
							<strong>Lower-impact fibers</strong>
							<span>Switch conventional cotton and virgin polyester to the modeled alternatives.</span>
						</button>
						<button type="button" class="action-card" @click="applyAction('energy')">
							<q-icon name="solar_power" size="32px" color="orange-8" />
							<strong>Cleaner production</strong>
							<span>Use renewable electricity and reduce process energy.</span>
						</button>
						<button type="button" class="action-card" @click="applyAction('care')">
							<q-icon name="local_laundry_service" size="32px" color="blue-7" />
							<strong>Cooler garment care</strong>
							<span>Wash cold, wash less often, and line dry.</span>
						</button>
						<button type="button" class="action-card" @click="applyAction('waste')">
							<q-icon name="content_cut" size="32px" color="deep-purple-6" />
							<strong>Lean cutting</strong>
							<span>Reduce cutting loss from the current value to 5%.</span>
						</button>
					</div>

					<div class="challenge-controls">
						<q-btn outline no-caps color="primary" icon="flag" label="Make current settings the baseline" @click="captureBaseline" />
						<q-btn flat no-caps color="grey-8" icon="restart_alt" label="Restore original example" @click="resetScenario" />
					</div>

					<q-card flat bordered class="detective-card">
						<q-card-section>
							<div class="card-title"><q-icon name="search" /> Hotspot detective</div>
							<p>Before looking back at the chart, predict the largest stage in your current scenario.</p>
							<div class="detective-options">
								<q-btn
									v-for="stage in result.stages"
									:key="stage.id"
									:color="guessedStage === stage.id ? 'primary' : 'grey-4'"
									:text-color="guessedStage === stage.id ? 'white' : 'grey-9'"
									no-caps
									unelevated
									:label="stage.label"
									@click="selectGuess(stage.id)"
								/>
							</div>
							<div v-if="guessChecked" class="guess-feedback" :class="{ 'guess-feedback--correct': guessIsCorrect }" aria-live="polite">
								<q-icon :name="guessIsCorrect ? 'check_circle' : 'lightbulb'" />
								{{ guessFeedback }}
							</div>
						</q-card-section>
					</q-card>
				</q-tab-panel>
			</q-tab-panels>

			<q-expansion-item
				group="method"
				icon="menu_book"
				label="Teaching notes, scope, and limitations"
				header-class="method-header"
				class="method-panel"
			>
				<q-card flat>
					<q-card-section class="method-copy">
						<p><strong>System boundary:</strong> raw material input, manufacturing electricity, one main freight leg, lifetime care, and one end-of-life pathway.</p>
						<p><strong>Functional unit:</strong> one finished garment used for the number of wears selected by the learner. “Per wear” divides the life-cycle total by lifetime wears.</p>
						<p><strong>What is simplified:</strong> trims, packaging, retail, chemical auxiliaries, water scarcity, land use, microfiber release, capital equipment, multi-leg logistics, allocation, and avoided-product credits are outside this classroom model.</p>
						<p><strong>Default data:</strong> {{ DISCLAIMER }}</p>
						<p>
							The activity structure adapts the CA-ROTEL Studio idea of a short brief, editable exploration, learner preview,
							and feedback. This is a standalone web app; it does not export SCORM or connect to an LMS.
						</p>
					</q-card-section>
				</q-card>
			</q-expansion-item>
		</main>
	</div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import VueApexCharts from "vue3-apexcharts";
import Toolbar from "@/components/tools/HomeToolBar.vue";
import {
	cloneScenario,
	createDefaultScenario,
	DISCLAIMER,
	END_OF_LIFE_OPTIONS,
	GARMENT_PRESETS,
	MANUFACTURING_REGIONS,
	MATERIALS,
	STAGE_META,
	TRANSPORT_MODES,
	WASH_TEMPERATURES,
} from "./apparelCarbonData.js";
import { calculateFootprint, compareFootprints, getHotspotHint } from "./apparelCarbonEngine.js";

export default {
	name: "ApparelCarbonLab",
	components: {
		Toolbar,
		apexchart: VueApexCharts,
	},
	setup() {
		const original = createDefaultScenario();
		const scenario = reactive(cloneScenario(original));
		const baselineScenario = reactive(cloneScenario(original));
		const activeTab = ref("garment");
		const guessedStage = ref("");
		const guessChecked = ref(false);
		const appliedActions = ref([]);

		const result = computed(() => calculateFootprint(scenario));
		const baselineResult = computed(() => calculateFootprint(baselineScenario));
		const comparison = computed(() => compareFootprints(baselineResult.value, result.value));
		const hotspotHint = computed(() => getHotspotHint(result.value.biggestStage.id));
		const challengeProgress = computed(() =>
			Math.max(0, Math.min(1, comparison.value.reductionPercent / 30))
		);

		const materialOptions = MATERIALS.map((item) => ({
			value: item.value,
			label: `${item.label} · ${item.factor} kg CO2e/kg`,
		}));

		const stageChartSeries = computed(() => result.value.stages.map((stage) => stage.emissions));
		const stageChartOptions = computed(() => ({
			labels: result.value.stages.map((stage) => stage.label),
			colors: result.value.stages.map((stage) => stage.color),
			legend: { position: "bottom" },
			dataLabels: {
				enabled: true,
				formatter(value) {
					return `${Math.round(value)}%`;
				},
			},
			plotOptions: { pie: { donut: { size: "58%" } } },
			tooltip: { y: { formatter: (value) => `${Number(value).toFixed(3)} kg CO2e` } },
			chart: { toolbar: { show: false } },
		}));

		const materialChartSeries = computed(() => [
			{
				name: "kg CO2e",
				data: result.value.materialBreakdown.map((item) => item.emissions),
			},
		]);
		const materialChartOptions = computed(() => ({
			chart: { toolbar: { show: false } },
			plotOptions: { bar: { horizontal: true, borderRadius: 5, distributed: true } },
			colors: result.value.materialBreakdown.map((item) => item.color),
			xaxis: {
				categories: result.value.materialBreakdown.map((item) => item.label),
				title: { text: "kg CO2e" },
			},
			legend: { show: false },
			dataLabels: { enabled: false },
			tooltip: { y: { formatter: (value) => `${Number(value).toFixed(3)} kg CO2e` } },
		}));

		const guessIsCorrect = computed(() => guessedStage.value === result.value.biggestStage.id);
		const guessFeedback = computed(() => {
			if (guessIsCorrect.value) {
				return `Correct — ${result.value.biggestStage.label} is the current hotspot.`;
			}
			return `The current hotspot is ${result.value.biggestStage.label}. Change an input and try again.`;
		});

		function applyGarmentPreset(value) {
			const preset = GARMENT_PRESETS.find((item) => item.value === value);
			if (!preset) return;
			scenario.weightGrams = preset.weightGrams;
			scenario.lifetimeWears = preset.wears;
		}

		function addMaterial() {
			if (scenario.materials.length >= 6) return;
			const used = scenario.materials.map((row) => row.material);
			const next = MATERIALS.find((material) => !used.includes(material.value)) || MATERIALS[0];
			scenario.materials.push({ material: next.value, percentage: 0 });
		}

		function removeMaterial(index) {
			if (scenario.materials.length > 1) scenario.materials.splice(index, 1);
		}

		function stageValue(...stageIds) {
			const value = result.value.stages
				.filter((stage) => stageIds.includes(stage.id))
				.reduce((sum, stage) => sum + stage.emissions, 0);
			return Number(value).toFixed(3);
		}

		function formatNumber(value, places = 2) {
			return Number(value || 0).toLocaleString(undefined, {
				minimumFractionDigits: places,
				maximumFractionDigits: places,
			});
		}

		function goTo(tab) {
			activeTab.value = tab;
			if (typeof window !== "undefined") window.scrollTo({ top: 360, behavior: "smooth" });
		}

		function replaceReactive(target, source) {
			Object.keys(target).forEach((key) => delete target[key]);
			Object.assign(target, cloneScenario(source));
		}

		function resetScenario() {
			replaceReactive(scenario, original);
			replaceReactive(baselineScenario, original);
			appliedActions.value = [];
			guessedStage.value = "";
			guessChecked.value = false;
		}

		function captureBaseline() {
			replaceReactive(baselineScenario, scenario);
			appliedActions.value = [];
		}

		function applyAction(action) {
			if (action === "materials") {
				scenario.materials.forEach((row) => {
					if (row.material === "cotton") row.material = "organic-cotton";
					if (row.material === "polyester") row.material = "recycled-polyester";
				});
			}
			if (action === "energy") {
				scenario.manufacturingRegion = "renewable";
				scenario.manufacturingEnergyKwhPerKg = Math.min(scenario.manufacturingEnergyKwhPerKg, 6);
			}
			if (action === "care") {
				scenario.washTemperature = "cold";
				scenario.dryerSharePercent = 0;
				scenario.wearsBetweenWashes = Math.max(scenario.wearsBetweenWashes, 3);
			}
			if (action === "waste") scenario.cuttingWastePercent = 5;
			if (!appliedActions.value.includes(action)) appliedActions.value.push(action);
		}

		function selectGuess(stageId) {
			guessedStage.value = stageId;
			guessChecked.value = true;
		}

		return {
			DISCLAIMER,
			END_OF_LIFE_OPTIONS,
			GARMENT_PRESETS,
			MANUFACTURING_REGIONS,
			STAGE_META,
			TRANSPORT_MODES,
			WASH_TEMPERATURES,
			activeTab,
			addMaterial,
			applyAction,
			applyGarmentPreset,
			baselineResult,
			captureBaseline,
			challengeProgress,
			comparison,
			formatNumber,
			goTo,
			guessChecked,
			guessFeedback,
			guessIsCorrect,
			guessedStage,
			hotspotHint,
			materialChartOptions,
			materialChartSeries,
			materialOptions,
			removeMaterial,
			resetScenario,
			result,
			scenario,
			selectGuess,
			stageChartOptions,
			stageChartSeries,
			stageValue,
		};
	},
};
</script>

<style scoped>
.carbon-lab-page {
	min-height: 100vh;
	background: #f5f7f4;
	color: #20312f;
	font-family: Inter, Avenir, Helvetica, Arial, sans-serif;
}

.hero-section {
	position: relative;
	overflow: hidden;
	background:
		radial-gradient(circle at 84% 18%, rgba(204, 255, 144, 0.28), transparent 24%),
		linear-gradient(130deg, #173f3a 0%, #28685f 55%, #547f50 100%);
	color: white;
	padding: 88px 24px 76px;
}

.hero-section::after {
	content: "CO₂e";
	position: absolute;
	right: 5%;
	top: 7%;
	font-size: clamp(80px, 15vw, 210px);
	font-weight: 800;
	color: rgba(255, 255, 255, 0.06);
	line-height: 1;
}

.hero-content {
	position: relative;
	z-index: 1;
	max-width: 1160px;
	margin: 0 auto;
}

.eyebrow,
.step-kicker {
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.16em;
}

.hero-section h1 {
	font-size: clamp(38px, 6vw, 72px);
	line-height: 1.04;
	margin: 12px 0 18px;
	max-width: 820px;
}

.hero-copy {
	font-size: clamp(17px, 2vw, 23px);
	line-height: 1.55;
	max-width: 780px;
	color: rgba(255, 255, 255, 0.88);
}

.lab-shell {
	max-width: 1200px;
	margin: -30px auto 0;
	padding: 0 24px 70px;
	position: relative;
	z-index: 2;
}

.formula-banner {
	background: #eef7ed;
	border: 1px solid #d4e7d1;
	box-shadow: 0 10px 28px rgba(24, 54, 48, 0.08);
	margin-bottom: 20px;
}

.lab-tabs {
	border-radius: 14px 14px 0 0;
	box-shadow: 0 8px 28px rgba(24, 54, 48, 0.08);
	overflow: hidden;
}

.panel-wrap {
	background: #fff;
	border-radius: 0 0 14px 14px;
	box-shadow: 0 14px 34px rgba(24, 54, 48, 0.08);
}

.panel-wrap :deep(.q-tab-panel) {
	padding: 36px;
}

.section-heading {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 24px;
	margin-bottom: 28px;
}

.step-kicker {
	color: #4e7d50;
}

.section-heading h2,
.challenge-hero h2 {
	font-size: clamp(26px, 3vw, 38px);
	line-height: 1.15;
	margin: 6px 0 8px;
}

.section-heading p,
.challenge-hero p {
	color: #60706e;
	font-size: 16px;
	margin: 0;
}

.two-column-grid,
.chart-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 20px;
}

.lifecycle-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 18px;
}

.input-card,
.chart-card,
.formula-card,
.detective-card {
	border-radius: 14px;
	border-color: #dce5df;
}

.input-card :deep(.q-card__section),
.chart-card :deep(.q-card__section),
.formula-card :deep(.q-card__section),
.detective-card :deep(.q-card__section) {
	padding: 24px;
}

.card-title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 19px;
	font-weight: 750;
}

.micro-explanation {
	margin-top: 16px;
	padding: 12px 14px;
	background: #f3f6f3;
	border-radius: 8px;
	font-size: 13px;
	line-height: 1.5;
	color: #64716f;
}

.material-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 10px 0;
}

.material-select {
	flex: 1;
}

.material-percent {
	width: 112px;
}

.mix-total,
.slider-label {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.mix-total {
	margin: 16px 0 8px;
	color: #287348;
}

.mix-total--error,
.validation-copy {
	color: #a55b00;
}

.validation-copy {
	font-size: 12px;
	margin-top: 8px;
}

.slider-label {
	font-size: 13px;
	margin-top: 24px;
}

.stage-card {
	position: relative;
	overflow: hidden;
}

.stage-number {
	position: absolute;
	right: 18px;
	top: 8px;
	font-size: 52px;
	line-height: 1;
	font-weight: 800;
	color: #edf2ee;
}

.stage-live {
	margin-top: 18px;
	padding-top: 14px;
	border-top: 1px solid #e4eae5;
	color: #275c45;
	font-weight: 800;
}

.compact-inputs {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
}

.panel-actions {
	display: flex;
	justify-content: flex-end;
	margin-top: 28px;
}

.panel-actions--split {
	justify-content: space-between;
}

.warning-banner {
	background: #fff3dc;
	color: #7b4c00;
}

.result-summary-grid {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 14px;
	margin-bottom: 22px;
}

.metric-card {
	background: #f1f5f2;
	border-radius: 12px;
	padding: 22px;
}

.metric-card--primary {
	background: #1f5b50;
	color: white;
}

.metric-label,
.comparison-label {
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	opacity: 0.72;
}

.metric-value {
	font-size: 34px;
	font-weight: 850;
	line-height: 1.1;
	margin-top: 12px;
}

.metric-value--text {
	font-size: 24px;
}

.metric-unit {
	font-size: 12px;
	margin-top: 6px;
	opacity: 0.76;
}

.chart-card {
	margin-bottom: 22px;
}

.formula-table-wrap {
	overflow-x: auto;
}

.formula-table {
	width: 100%;
	border-collapse: collapse;
	min-width: 760px;
}

.formula-table th,
.formula-table td {
	padding: 14px 12px;
	border-top: 1px solid #e3e9e4;
	text-align: left;
	vertical-align: top;
}

.formula-table th {
	font-size: 12px;
	color: #687572;
	text-transform: uppercase;
}

.formula-table td small {
	display: block;
	max-width: 300px;
	margin-top: 5px;
	color: #75817f;
	line-height: 1.35;
}

.hotspot-banner {
	background: #fff8df;
	color: #564400;
}

.challenge-hero {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 24px;
	margin-bottom: 18px;
}

.challenge-score {
	width: 130px;
	height: 130px;
	border-radius: 50%;
	background: #edf3ef;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
}

.challenge-score div {
	font-size: 34px;
	font-weight: 850;
}

.challenge-score span {
	font-size: 12px;
	text-transform: uppercase;
	letter-spacing: 0.08em;
}

.challenge-score--complete {
	background: #dff3df;
	color: #1c6d38;
}

.success-banner {
	background: #e0f4e4;
	color: #1d6334;
}

.comparison-grid {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 12px;
	margin: 20px 0;
}

.comparison-card {
	padding: 24px;
	text-align: center;
	border-radius: 12px;
}

.comparison-card--current {
	border-color: #4f8060;
	background: #f1f8f2;
}

.comparison-value {
	font-size: 34px;
	font-weight: 850;
	margin-top: 6px;
}

.action-grid {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
	margin: 24px 0;
}

.action-card {
	appearance: none;
	background: white;
	border: 1px solid #dce5df;
	border-radius: 12px;
	padding: 20px;
	text-align: left;
	cursor: pointer;
	color: inherit;
	font: inherit;
	transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.action-card:hover,
.action-card:focus-visible {
	transform: translateY(-3px);
	box-shadow: 0 10px 24px rgba(30, 72, 60, 0.12);
	border-color: #77a184;
	outline: none;
}

.action-card strong,
.action-card span {
	display: block;
}

.action-card strong {
	font-size: 16px;
	margin: 14px 0 6px;
}

.action-card span {
	font-size: 13px;
	line-height: 1.45;
	color: #6b7775;
}

.challenge-controls,
.detective-options {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
}

.challenge-controls {
	justify-content: space-between;
	margin-bottom: 24px;
}

.guess-feedback {
	margin-top: 16px;
	padding: 12px;
	background: #fff4d5;
	border-radius: 8px;
	color: #795900;
}

.guess-feedback--correct {
	background: #e2f4e5;
	color: #1c6d38;
}

.method-panel {
	margin-top: 20px;
	background: white;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(24, 54, 48, 0.06);
}

.method-panel :deep(.method-header) {
	min-height: 64px;
	font-weight: 750;
}

.method-copy {
	padding: 4px 24px 22px;
	color: #5d6a68;
	line-height: 1.6;
}

@media (max-width: 900px) {
	.two-column-grid,
	.chart-grid,
	.lifecycle-grid {
		grid-template-columns: 1fr;
	}

	.result-summary-grid,
	.action-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.lab-tabs :deep(.q-tab__label) {
		font-size: 11px;
	}
}

@media (max-width: 600px) {
	.hero-section {
		padding: 58px 18px 55px;
	}

	.lab-shell {
		padding: 0 10px 45px;
	}

	.panel-wrap :deep(.q-tab-panel) {
		padding: 24px 14px;
	}

	.lab-tabs :deep(.q-tab__label) {
		display: none;
	}

	.section-heading,
	.challenge-hero {
		flex-direction: column;
	}

	.result-summary-grid,
	.action-grid {
		grid-template-columns: 1fr;
	}

	.material-row {
		align-items: flex-start;
		flex-wrap: wrap;
	}

	.material-select {
		flex-basis: calc(100% - 124px);
	}

	.compact-inputs {
		grid-template-columns: 1fr;
	}

	.challenge-score {
		width: 105px;
		height: 105px;
	}

	.comparison-grid {
		grid-template-columns: 1fr;
	}

	.comparison-grid > .q-icon {
		transform: rotate(90deg);
		justify-self: center;
	}

	.panel-actions--split,
	.challenge-controls {
		align-items: stretch;
		flex-direction: column;
	}
}
</style>
